import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useTranslation } from 'react-i18next';
import {
  generateSuggestedPassword,
  PASSWORD_RULES,
} from '../../utils/password.helpers';
import { CONTENT_FONT_FAMILY_VAR } from '../../constants/fonts';

interface PasswordSetupSectionProps {
  password: string;
  passwordConfirm: string;
  onPasswordChange: (value: string) => void;
  onPasswordConfirmChange: (value: string) => void;
  disabled?: boolean;
  onSuggested?: () => void;
}

const PasswordSetupSection: React.FC<PasswordSetupSectionProps> = ({
  password,
  passwordConfirm,
  onPasswordChange,
  onPasswordConfirmChange,
  disabled = false,
  onSuggested,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [touched, setTouched] = useState({ password: false, confirm: false });

  const passwordsMatch = password === passwordConfirm;
  const showConfirmError = touched.confirm && passwordConfirm.length > 0 && !passwordsMatch;

  const handleSuggest = () => {
    const suggested = generateSuggestedPassword();
    onPasswordChange(suggested);
    onPasswordConfirmChange(suggested);
    setTouched({ password: true, confirm: true });
    onSuggested?.();
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2.5,
        borderRadius: '16px',
        border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)}`,
        bgcolor: theme.palette.background.paper,
        boxShadow: isDark
          ? `0 8px 24px ${alpha('#000', 0.28)}`
          : `0 8px 24px ${alpha('#0f172a', 0.05)}`,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: { xs: 2.25, sm: 3 }, '&:last-child': { pb: { xs: 2.25, sm: 3 } } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
          sx={{
            mb: 2.5,
            pb: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                flexShrink: 0,
              }}
            >
              <LockOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: CONTENT_FONT_FAMILY_VAR,
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  lineHeight: 1.3,
                  color: 'text.primary',
                }}
              >
                {t('users.form.passwordTitle')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: CONTENT_FONT_FAMILY_VAR,
                  mt: 0.35,
                  fontSize: '0.875rem',
                  lineHeight: 1.45,
                  color: 'text.secondary',
                }}
              >
                {t('users.form.passwordSubtitle')}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AutoAwesomeOutlinedIcon />}
            disabled={disabled}
            onClick={handleSuggest}
            sx={{
              textTransform: 'none',
              flexShrink: 0,
              borderRadius: '10px',
              fontWeight: 600,
            }}
          >
            {t('users.form.suggestPassword')}
          </Button>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('users.form.password')}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              size="small"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
              required
              disabled={disabled}
              autoComplete="new-password"
              error={
                touched.password &&
                Boolean(password) &&
                !PASSWORD_RULES.every((r) => r.test(password))
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label={
                        showPassword
                          ? t('users.form.hidePassword')
                          : t('users.form.showPassword')
                      }
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('users.form.confirmPassword')}
              type={showConfirm ? 'text' : 'password'}
              fullWidth
              size="small"
              value={passwordConfirm}
              onChange={(e) => onPasswordConfirmChange(e.target.value)}
              onBlur={() => setTouched((prev) => ({ ...prev, confirm: true }))}
              required
              disabled={disabled}
              autoComplete="new-password"
              error={showConfirmError}
              helperText={
                showConfirmError ? t('users.form.passwordsDoNotMatch') : ' '
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label={
                        showConfirm
                          ? t('users.form.hidePassword')
                          : t('users.form.showPassword')
                      }
                      onClick={() => setShowConfirm((v) => !v)}
                    >
                      {showConfirm ? (
                        <VisibilityOffOutlinedIcon fontSize="small" />
                      ) : (
                        <VisibilityOutlinedIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 1.5,
            p: 2,
            borderRadius: 2,
            bgcolor: 'action.hover',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            {t('users.form.passwordRequirements')}
          </Typography>
          <Stack spacing={0.75}>
            {PASSWORD_RULES.map((rule) => {
              const met = password.length > 0 && rule.test(password);
              return (
                <Stack key={rule.id} direction="row" spacing={1} alignItems="center">
                  {met ? (
                    <CheckCircleOutlineIcon color="success" sx={{ fontSize: 18 }} />
                  ) : (
                    <RadioButtonUncheckedIcon color="disabled" sx={{ fontSize: 18 }} />
                  )}
                  <Typography
                    variant="body2"
                    color={met ? 'success.main' : 'text.secondary'}
                    sx={{ lineHeight: 1.4 }}
                  >
                    {t(`users.form.passwordRules.${rule.id}`)}
                  </Typography>
                </Stack>
              );
            })}
            {passwordConfirm.length > 0 && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
                {passwordsMatch ? (
                  <CheckCircleOutlineIcon color="success" sx={{ fontSize: 18 }} />
                ) : (
                  <RadioButtonUncheckedIcon color="disabled" sx={{ fontSize: 18 }} />
                )}
                <Typography
                  variant="body2"
                  color={passwordsMatch ? 'success.main' : 'text.secondary'}
                >
                  {t('users.form.passwordsMatch')}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PasswordSetupSection;
